import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Modal, Dropdown, Typography, Space, Menu, Input, Avatar, Button, Tooltip, Tag, Divider } from "antd";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { BiArrowBack, BiSearchAlt } from "react-icons/bi";
import { getChatById, archiveChatById } from "@api/copilots/chat";
import { SuccessNotification, ErrorNotification } from "@components";
import logoSmall from "@assets/images/logo/logo.png";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useTranslation } from "react-i18next";

const items = [
  {
    key: "1",
    label: "Item 1",
  },
  {
    key: "2",
    label: "Item 2",
  },
  {
    key: "3",
    label: "Item 3",
  },
];

const { Title, Text } = Typography;
const { Search } = Input;

const ChatDetails = () => {
  const { user } = useSelector((state) => state.auth);
  const { id } = useParams();
  const history = useHistory();
  const [chat, setChat] = useState(null);
  const [selectedDropdownItem, setSelectedDropdownItem] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [searchedText, setSearchedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [highlightedMessages, setHighlightedMessages] = useState(new Set());
  const messagesEndRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    handleFetchChatById();
  }, [id]);

  useEffect(() => {
    if (chat) {
      scrollToBottom();
    }
  }, [chat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Handle fetch chat by id
  const handleFetchChatById = async () => {
    setLoading(true);
    try {
      const { data } = await getChatById(id);
      setChat(data);
    } catch (error) {
      ErrorNotification("Failed to load chat details");
      console.log("GET CHAT BY ID ERROR", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle chat archive
  const handleChatArchive = (id) => {
    // Confirm delete
    Modal.confirm({
      title: "Are you sure you want to archive this chat?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          // Archive event
          await archiveChatById(id);
          // Show success notification
          SuccessNotification("Chat archived successfully");

          // Redirect to history page
          history.push("/history");
        } catch (error) {
          // Show error notification
          ErrorNotification("Error archiving chat");
        }
      },
    });
  };

  // Handle dropdown item click
  const handleDropdownItemClick = (item) => {
    setSelectedDropdownItem(item);
  };
  const menu = (
    <Menu>
      <Menu.Item key="archive" onClick={() => handleChatArchive(chat?._id)}>
        Archive
      </Menu.Item>

      {items.map((item) => (
        <Menu.Item key={item.key} onClick={() => handleDropdownItemClick(item)}>
          {item.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const showModal = () => {
    setIsVisible(true);
  };

  const handleCancel = () => {
    setIsVisible(false);
    setSearchedText("");
    setHighlightedMessages(new Set());
  };

  // Handle search functionality
  const handleSearch = (value) => {
    setSearchedText(value);
    if (!value.trim()) {
      setHighlightedMessages(new Set());
      return;
    }

    const matches = new Set();
    chat?.messages?.forEach((message, index) => {
      const searchValue = value.toLowerCase();
      const inputText = (message.input || "").toLowerCase();
      const responseText = (message.generated_text || "").toLowerCase();

      if (inputText.includes(searchValue) || responseText.includes(searchValue)) {
        matches.add(index);
      }
    });

    setHighlightedMessages(matches);
  };

  // Highlight text function
  const highlightText = (text, searchText) => {
    if (!searchText || !text) return text;

    const regex = new RegExp(`(${searchText})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#c4b5fd', fontWeight: 'bold', padding: '2px 4px', borderRadius: '4px' }}>
          {part}
        </span>
      ) : part
    );
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) {
      return { date: 'Unknown date', time: 'Unknown time' };
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return { date: 'Invalid date', time: 'Invalid time' };
      }
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };
    } catch (error) {
      console.error('Date formatting error:', error);
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };

  // Copy message function
  const copyMessage = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      SuccessNotification("Message copied to clipboard");
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)"
      }}>
        <Card style={{ textAlign: 'center', borderRadius: '12px', padding: '40px', boxShadow: '0 4px 20px rgba(139, 92, 246, 0.1)', border: '1px solid #e9d5ff' }}>
          <div style={{ fontSize: '32px', marginBottom: '24px', color: '#8B5CF6' }}>📊</div>
          <Title level={4} style={{ color: '#1e293b', marginBottom: '8px' }}>Loading Chat Analytics</Title>
          <Text style={{ color: '#64748b' }}>Retrieving conversation data...</Text>
        </Card>
      </div>
    );
  }

  const { date, time } = chat ? formatDateTime(chat.createdAt) : { date: '...', time: '...' };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)",
        minHeight: "100vh",
        padding: "20px"
      }}
    >
      {/* Modern Header */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "16px 20px",
          marginBottom: "16px",
          boxShadow: "0 2px 8px rgba(139, 92, 246, 0.08)",
          border: "1px solid #e9d5ff"
        }}
      >
        <Row align="middle" justify="space-between">
          <Col flex="auto">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Button
                type="text"
                size="small"
                icon={<BiArrowBack size={16} />}
                onClick={() => history.goBack()}
                style={{
                  borderRadius: "8px",
                  background: "#faf5ff",
                  border: "1px solid #e9d5ff",
                  height: "32px",
                  width: "32px",
                  padding: 0,
                  color: "#8B5CF6"
                }}
              />
              <Avatar
                src={user?.profilePicture || logoSmall}
                size={32}
                style={{
                  border: "2px solid #e9d5ff",
                  boxShadow: "0 2px 8px rgba(139, 92, 246, 0.1)"
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                  <Title level={5} style={{ margin: 0, color: "#1e293b", fontSize: "15px" }}>
                    {chat?.chatName || "Untitled Chat"}
                  </Title>
                  <Tag color="purple" style={{ borderRadius: "6px", fontSize: "10px", padding: "0 6px", height: "18px", lineHeight: "18px" }}>
                    ID: {chat?._id?.slice(-6)}
                  </Tag>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Text style={{ fontSize: "11px", color: "#64748b" }}>
                    📅 {date} 🕒 {time}
                  </Text>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "10px", color: "#64748b", background: "#faf5ff", padding: "2px 6px", borderRadius: "4px", border: "1px solid #e9d5ff" }}>
                      📨 {chat?.messages?.length || 0}
                    </span>
                    <span style={{ fontSize: "10px", color: "#64748b", background: "#faf5ff", padding: "2px 6px", borderRadius: "4px", border: "1px solid #e9d5ff" }}>
                      🔄 {Math.ceil((chat?.messages?.length || 0) / 2)}
                    </span>
                    <span style={{ fontSize: "10px", color: "#64748b", background: "#faf5ff", padding: "2px 6px", borderRadius: "4px", border: "1px solid #e9d5ff" }}>
                      📝 {Math.round((chat?.messages?.reduce((total, msg) => total + (msg?.input?.split(' ').length || 0) + (msg?.generated_text?.split(' ').length || 0), 0) || 0) / 100) * 100}w
                    </span>
                    {chat?.useful_messages?.length > 0 && (
                      <span style={{ fontSize: "10px", color: "#059669", background: "rgba(16, 185, 129, 0.1)", padding: "2px 6px", borderRadius: "4px", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                        👍 {chat.useful_messages.length}
                      </span>
                    )}
                    {chat?.poor_messages?.length > 0 && (
                      <span style={{ fontSize: "10px", color: "#dc2626", background: "rgba(220, 38, 38, 0.1)", padding: "2px 6px", borderRadius: "4px", border: "1px solid rgba(220, 38, 38, 0.2)" }}>
                        👎 {chat.poor_messages.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Col>

          <Col>
            <Space size="small">
              <Tooltip title="Search messages">
                <Button
                  icon={<BiSearchAlt size={16} />}
                  onClick={showModal}
                  style={{
                    borderRadius: "8px",
                    background: "#faf5ff",
                    border: "1px solid #e9d5ff",
                    color: "#8B5CF6"
                  }}
                />
              </Tooltip>
              <Dropdown overlay={menu}>
                <Button
                  icon={<BsThreeDotsVertical size={16} />}
                  style={{
                    borderRadius: "8px",
                    background: "#faf5ff",
                    border: "1px solid #e9d5ff",
                    color: "#64748b"
                  }}
                />
              </Dropdown>
            </Space>
          </Col>
        </Row>

        {/* Search Results */}
        {highlightedMessages.size > 0 && (
          <div style={{
            marginTop: "12px",
            padding: "10px 14px",
            background: "rgba(139, 92, 246, 0.08)",
            borderRadius: "8px",
            border: "1px solid #e9d5ff"
          }}>
            <Text style={{ fontSize: "12px", color: "#7c3aed" }}>
              🔍 Found {highlightedMessages.size} message(s) containing "{searchedText}"
            </Text>
          </div>
        )}
      </div>


      {/* Modern Chat Container */}
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          height: "calc(100vh - 140px)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 2px 8px rgba(139, 92, 246, 0.08)",
          border: "1px solid #e9d5ff",
          overflow: "hidden"
        }}
      >
        {/* Messages Container */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px"
          }}
        >
          {chat?.messages?.length > 0 ? (
            chat.messages.map((message, index) => (
              <div
                key={index}
                style={{
                  background: highlightedMessages.has(index)
                    ? "linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%)"
                    : "transparent",
                  borderRadius: highlightedMessages.has(index) ? "10px" : "0",
                  padding: highlightedMessages.has(index) ? "14px" : "0",
                  border: highlightedMessages.has(index) ? "1px solid #e9d5ff" : "none"
                }}
              >
                {/* User Message */}
                <div style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "10px"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "10px",
                    maxWidth: "75%"
                  }}>
                    <div
                      style={{
                        background: "linear-gradient(135deg, #8B5CF6 0%, #a78bfa 100%)",
                        color: "white",
                        padding: "12px 16px",
                        borderRadius: "12px 12px 4px 12px",
                        boxShadow: "0 2px 8px rgba(139, 92, 246, 0.25)",
                        wordBreak: "break-word",
                        fontSize: "13px",
                        lineHeight: "1.5",
                        position: "relative"
                      }}
                    >
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {highlightText(message.input, searchedText)}
                      </div>
                      <Tooltip title="Copy message">
                        <Button
                          size="small"
                          type="text"
                          onClick={() => copyMessage(message.input)}
                          style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            color: "rgba(255,255,255,0.7)",
                            fontSize: "10px"
                          }}
                        >
                          📋
                        </Button>
                      </Tooltip>
                    </div>
                    <Avatar
                      size={32}
                      style={{
                        background: "linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)",
                        border: "2px solid #fff",
                        boxShadow: "0 2px 6px rgba(139, 92, 246, 0.2)",
                        flexShrink: 0,
                        fontWeight: "bold",
                        fontSize: "12px"
                      }}
                    >
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </Avatar>
                  </div>
                </div>

                {/* Bot Response */}
                <div style={{
                  display: "flex",
                  justifyContent: "flex-start"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "10px",
                    maxWidth: "75%"
                  }}>
                    <Avatar
                      src={logoSmall}
                      size={32}
                      style={{
                        flexShrink: 0,
                        border: "2px solid #fff",
                        boxShadow: "0 2px 6px rgba(139, 92, 246, 0.1)"
                      }}
                    />
                    <div
                      style={{
                        background: "#faf5ff",
                        color: "#1e293b",
                        padding: "12px 16px",
                        borderRadius: "12px 12px 12px 4px",
                        boxShadow: "0 2px 6px rgba(139, 92, 246, 0.06)",
                        wordBreak: "break-word",
                        fontSize: "13px",
                        lineHeight: "1.6",
                        border: "1px solid #e9d5ff",
                        position: "relative"
                      }}
                    >
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {highlightText(message.generated_text, searchedText)}
                      </div>
                      <Tooltip title="Copy message">
                        <Button
                          size="small"
                          type="text"
                          onClick={() => copyMessage(message.generated_text)}
                          style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            color: "rgba(0,0,0,0.4)",
                            fontSize: "10px"
                          }}
                        >
                          📋
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Message Divider */}
                {index < chat.messages.length - 1 && (
                  <Divider style={{ margin: "16px 0", borderColor: "#e9d5ff" }} />
                )}
              </div>
            ))
          ) : (
            <div style={{
              textAlign: "center",
              padding: "60px",
              color: "#64748b"
            }}>
              <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}>💬</div>
              <Title level={4} style={{ color: "#94a3b8", margin: "8px 0" }}>No Messages Found</Title>
              <Text style={{ color: "#94a3b8" }}>This chat doesn't contain any messages.</Text>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Improved Search Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#1e293b" }}>
            <BiSearchAlt size={20} color="#8B5CF6" />
            <span>Search Messages</span>
          </div>
        }
        visible={isVisible}
        onCancel={handleCancel}
        footer={null}
        width={500}
        style={{ top: 100 }}
        bodyStyle={{ padding: "20px" }}
      >
        <Search
          placeholder="Type to search in messages..."
          value={searchedText}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ marginBottom: "16px" }}
          size="large"
          allowClear
        />
        {highlightedMessages.size > 0 && (
          <div style={{
            padding: "12px",
            background: "rgba(139, 92, 246, 0.08)",
            borderRadius: "8px",
            marginTop: "16px",
            border: "1px solid #e9d5ff"
          }}>
            <Text style={{ color: "#7c3aed" }}>
              ✨ Found {highlightedMessages.size} matching message{highlightedMessages.size !== 1 ? 's' : ''}
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ChatDetails;
